import { useEffect, useRef, useState } from 'react';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { useLanguage, useTranslation } from '../i18n/useTranslation';

const languages = [
  { code: 'fr', label: 'FR' },
  { code: 'de', label: 'DE' },
  { code: 'en', label: 'EN' },
  { code: 'it', label: 'IT' },
] as const;

interface LanguageSwitcherProps {
  variant?: 'dropdown' | 'list';
  appearance?: 'default' | 'auth';
  onSelect?: () => void;
}

export function LanguageSwitcher({
  variant = 'dropdown',
  appearance = 'default',
  onSelect,
}: LanguageSwitcherProps = {}) {
  const { language, changeLanguage } = useLanguage();
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const current = languages.find((l) => l.code === language) ?? languages[0];

  useEffect(() => {
    if (!open) return;
    function onDocClick(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function selectLanguage(code: (typeof languages)[number]['code']) {
    void changeLanguage(code);
    setOpen(false);
    onSelect?.();
  }

  if (variant === 'list') {
    return (
      <div role="group" aria-label={t('language')} className="space-y-1">
        {languages.map(({ code }) => {
          const active = language === code;
          return (
            <button
              key={code}
              type="button"
              aria-pressed={active}
              onClick={() => selectLanguage(code)}
              className={`flex min-h-[44px] w-full cursor-pointer items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                active
                  ? 'bg-foreground/5 text-foreground'
                  : 'text-muted-foreground hover:bg-foreground/5 hover:text-foreground'
              }`}
            >
              <span>{t(`languages.${code}`)}</span>
              {active && <Check className="size-3.5" aria-hidden="true" />}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('language')}
        className={
          appearance === 'auth'
            ? 'flex min-h-9 cursor-pointer items-center gap-1.5 rounded-full border-2 border-[var(--auth-outline)] bg-white px-3.5 py-1.5 text-[13px] font-bold text-[var(--auth-dark)] transition-shadow hover:shadow-[3px_3px_0_var(--auth-outline)]'
            : 'flex min-h-[44px] cursor-pointer items-center gap-1.5 rounded-lg px-2 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground sm:min-h-0'
        }
      >
        <Globe className="size-4" aria-hidden="true" />
        <span>{current.label}</span>
        <ChevronDown
          className={`size-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <nav
          role="menu"
          aria-label="Language"
          className="absolute right-0 z-50 mt-1 min-w-[10rem] space-y-1 rounded-xl border border-border bg-background p-1.5 shadow-lg"
        >
          {languages.map(({ code }) => {
            const active = language === code;
            return (
              <button
                key={code}
                type="button"
                role="menuitem"
                onClick={() => selectLanguage(code)}
                className={`flex w-full items-center justify-between gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors min-h-[44px] cursor-pointer ${
                  active
                    ? 'bg-foreground/5 text-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-foreground/5'
                }`}
              >
                <span>{t(`languages.${code}`)}</span>
                {active && <Check className="size-3.5" aria-hidden="true" />}
              </button>
            );
          })}
        </nav>
      )}
    </div>
  );
}
